import { useState } from 'react'

import { Skeleton } from '@factorialco/deprecated-design-system'
import { F0Box } from '@factorialco/f0-react'
import { ProfileWorkDetailsCustomFieldsDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import { useIntersectionObserver } from 'usehooks-ts'

import { useQuery } from 'modules/core/hooks/useQuery'
import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import { filterCustomResourceFieldsDataByUsageGroup } from 'modules/custom_resources/lib/custom-resource-utils'
import i18n from 'modules/core/lib/i18n'

interface Props {
  employeeId: string
  currency?: string
  padFormDirection: 'vertical' | 'horizontal'
  boxed: boolean
}

const USAGE_GROUP_SLUGS = ['employees-questions', 'employee-profile']

const CustomFieldsSectionsSkeleton = () => {
  return (
    <F0Box display="flex" flexDirection="column" gap="lg" paddingX="4xl" paddingY="xl">
      <F0Box display="flex" flexDirection="row" gap="lg" justifyContent="between">
        <F0Box display="flex" flexDirection="column" gap="sm">
          <Skeleton width="s32" height="s32" />
          <Skeleton width="s200" height="s16" />
          <Skeleton width="s400" height="s12" />
        </F0Box>
        <F0Box display="flex" flexDirection="column" gap="md" width="96">
          <Skeleton width="s240" height="s12" />
          <Skeleton width="full" height="s32" />
          <Skeleton width="s200" height="s12" />
          <Skeleton width="full" height="s32" />
        </F0Box>
      </F0Box>
      <F0Box display="flex" flexDirection="row" gap="lg" justifyContent="between">
        <F0Box display="flex" flexDirection="column" gap="sm">
          <Skeleton width="s200" height="s16" />
          <Skeleton width="s400" height="s12" />
        </F0Box>
        <F0Box display="flex" flexDirection="column" gap="md" width="96">
          <Skeleton width="s240" height="s12" />
          <Skeleton width="full" height="s32" />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

const CustomFieldsSections = ({ employeeId, currency, padFormDirection, boxed }: Props) => {
  const [shouldFetch, setShouldFetch] = useState(false)
  const { ref } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '300px',
    onChange(isIntersecting) {
      if (isIntersecting) {
        setShouldFetch(true)
      }
    },
  })

  const { data, loading } = useQuery(ProfileWorkDetailsCustomFieldsDocument, {
    variables: {
      employeeId,
      usageGroupSlugs: USAGE_GROUP_SLUGS,
    },
    skip: !shouldFetch,
  })

  const customResourceFieldsData = compact(
    data?.customFields?.resourceFields?.filter((resourceField) => !!resourceField?.field) ?? []
  )
  const customValuesData = data?.employees?.employee?.values ?? []

  return (
    <div ref={ref} style={{ minHeight: shouldFetch ? undefined : 1 }}>
      {shouldFetch && loading && <CustomFieldsSectionsSkeleton />}
      {shouldFetch && !loading && (
        <>
          <CustomFieldsForm
            boxed={boxed}
            customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
              customResourceFieldsData,
              'employee-profile'
            )}
            customValuesData={customValuesData}
            description={i18n.t('employee_form.custom_fields.basic_information.description')}
            icon="input-field"
            padFormDirection={padFormDirection}
            title={i18n.t('employee_form.custom_fields.basic_information.title')}
            valuableId={employeeId}
            valuableType="Employee"
            currency={currency}
          />

          <CustomFieldsForm
            boxed={boxed}
            customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
              customResourceFieldsData,
              'employees-questions'
            )}
            customValuesData={customValuesData}
            padFormDirection={padFormDirection}
            valuableId={employeeId}
            valuableType="Employee"
            currency={currency}
          />
        </>
      )}
    </div>
  )
}

export default CustomFieldsSections
